import dayjs from 'dayjs';

/**
 * Utilidades para el módulo de Asistencia Laboral
 * Maneja el agrupamiento por trabajador y fecha, y el cálculo de horas trabajadas.
 */

/**
 * Calcula la diferencia entre dos horas en formato "HH:mm" o "HH:mm:ss".
 * Devuelve los minutos totales transcurridos y el texto formateado en "X h YY min".
 */
export const calcularDiferenciaHoras = (horaInicio, horaFin) => {
  if (!horaInicio || !horaFin) {
    return { minutos: null, texto: '—', esValido: false };
  }

  const parsearATotalMinutos = (strHora) => {
    const partes = strHora.split(':').map(Number);
    const horas = partes[0] || 0;
    const minutos = partes[1] || 0;
    const segundos = partes[2] || 0;
    return horas * 60 + minutos + segundos / 60;
  };

  const minInicio = parsearATotalMinutos(horaInicio);
  const minFin = parsearATotalMinutos(horaFin);
  const diffMinutos = Math.round(minFin - minInicio);

  if (diffMinutos < 0) {
    return {
      minutos: diffMinutos,
      texto: 'Horario inconsistente',
      esValido: false,
    };
  }

  const horas = Math.floor(diffMinutos / 60);
  const minutos = diffMinutos % 60;

  let texto = '';
  if (horas === 0 && minutos === 0) {
    texto = '< 1 min';
  } else if (horas === 0) {
    texto = `${minutos} min`;
  } else if (minutos === 0) {
    texto = `${horas} h`;
  } else {
    texto = `${horas} h ${minutos.toString().padStart(2, '0')} min`;
  }

  return {
    minutos: diffMinutos,
    texto,
    esValido: true,
  };
};

/**
 * Formatea una fecha YYYY-MM-DD a DD/MM/YYYY
 */
export const formatearFechaVisual = (fecha) => {
  if (!fecha) return '-';
  const partes = String(fecha).split('-');
  if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
  return fecha;
};

/**
 * Formatea una hora "HH:mm:ss" a "HH:mm hs"
 */
export const formatearHoraVisual = (hora) => {
  if (!hora) return '-';
  return `${hora.substring(0, 5)} hs`;
};

/**
 * Agrupa una lista plana de asistencias atómicas (con ENTRADA y SALIDA separadas)
 * en una lista de jornadas únicas por CUIL y Fecha.
 *
 * @param {Array} asistencias - Lista de registros devueltos por el backend
 * @returns {Array} Lista de jornadas consolidadas
 */
export const agruparAsistenciasPorJornada = (asistencias = []) => {
  if (!Array.isArray(asistencias) || asistencias.length === 0) {
    return [];
  }

  const todayStr = dayjs().format('YYYY-MM-DD');
  const jornadasMap = new Map();

  asistencias.forEach((item) => {
    const key = `${item.cuil}_${item.fecha}`;
    if (!jornadasMap.has(key)) {
      const nombreLimpio = item.nombre || '';
      const apellidoLimpio = item.apellido || '';
      const nombreCompleto = `${apellidoLimpio} ${nombreLimpio}`.trim() || 'Sin datos CiDi';

      jornadasMap.set(key, {
        id: key,
        cuil: item.cuil,
        fecha: item.fecha,
        nombre: nombreLimpio,
        apellido: apellidoLimpio,
        nombre_completo: nombreCompleto,
        correo: item.correo || null,
        celular: item.celular || null,
        es_empleado: item.es_empleado ?? 0,
        id_empleado: item.id_empleado || null,
        entrada: null,
        salida: null,
      });
    }

    const jornada = jornadasMap.get(key);
    if (item.tipo_registro === 'ENTRADA') {
      jornada.entrada = item;
    } else if (item.tipo_registro === 'SALIDA') {
      jornada.salida = item;
    }
  });

  return Array.from(jornadasMap.values())
    .map((jornada) => {
      let totalMinutos = null;
      let totalHorasTexto = '—';
      let estado = 'INCONSISTENTE';
      const esDiaPasado = jornada.fecha < todayStr;

      if (jornada.entrada && jornada.salida) {
        const resultadoDiff = calcularDiferenciaHoras(jornada.entrada.hora, jornada.salida.hora);
        totalMinutos = resultadoDiff.minutos;
        totalHorasTexto = resultadoDiff.texto;
        estado = resultadoDiff.esValido ? 'COMPLETO' : 'INCONSISTENTE';
      } else if (jornada.entrada && !jornada.salida) {
        if (esDiaPasado) {
          // Ya pasó el día: no puede estar "en curso", quedó sin salida registrada
          totalHorasTexto = '—';
          estado = 'SIN_SALIDA';
        } else {
          // Es hoy: la jornada está transcurriendo
          totalHorasTexto = 'En curso';
          estado = 'EN_CURSO';
        }
      } else if (!jornada.entrada && jornada.salida) {
        totalHorasTexto = 'Sin entrada';
        estado = 'INCONSISTENTE';
      }

      return {
        ...jornada,
        totalMinutos,
        totalHorasTexto,
        estado, // 'COMPLETO' | 'EN_CURSO' | 'SIN_SALIDA' | 'INCONSISTENTE'
      };
    })
    .sort((a, b) => {
      // Orden por fecha descendente
      if (a.fecha !== b.fecha) {
        return b.fecha.localeCompare(a.fecha);
      }
      // Dentro de la misma fecha, por hora de entrada o salida más reciente
      const horaA = a.entrada?.hora || a.salida?.hora || '00:00:00';
      const horaB = b.entrada?.hora || b.salida?.hora || '00:00:00';
      return horaB.localeCompare(horaA);
    });
};
